import { useEffect, useState } from 'react'

export type VisualViewport = {
	/** Visible height: shrinks when the on-screen keyboard opens. */
	height: number
	/** How far the browser panned the page to keep the focused field visible. */
	offsetTop: number
}

const read = (): VisualViewport => ({
	height: window.visualViewport?.height ?? window.innerHeight,
	offsetTop: window.visualViewport?.offsetTop ?? 0,
})

/**
 * The part of the page the participant can actually see.
 *
 * `window.innerHeight` (what `react-div-100vh` reads) does not change when the
 * iOS keyboard opens, so a bottom-pinned "Send" ends up under the keyboard and
 * the page pans the top bar away. Sizing the frame to the visual viewport and
 * following its offset keeps both the top bar and the action on screen.
 */
export const useVisualViewport = (): VisualViewport => {
	const [viewport, setViewport] = useState<VisualViewport>(read)

	useEffect(() => {
		// `scroll` fires on every frame of a pan; only a change re-renders.
		const update = () =>
			setViewport(current => {
				const next = read()
				return next.height === current.height &&
					next.offsetTop === current.offsetTop
					? current
					: next
			})
		const visual = window.visualViewport
		visual?.addEventListener('resize', update)
		visual?.addEventListener('scroll', update)
		window.addEventListener('resize', update)
		return () => {
			visual?.removeEventListener('resize', update)
			visual?.removeEventListener('scroll', update)
			window.removeEventListener('resize', update)
		}
	}, [])

	return viewport
}

export default useVisualViewport
